import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useGameStore, useGameActions } from '../hooks/useGameStore';
import { ENDINGS } from '../data/endings';

export const EndingScreen: React.FC = () => {
  const state = useGameStore();
  const actions = useGameActions();
  const [visibleLines, setVisibleLines] = useState(0);
  const [showCredits, setShowCredits] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const ending = state.endingType ? ENDINGS[state.endingType] : null;
  const lines = useMemo(() => ending?.text ?? [], [ending]);

  useEffect(() => {
    if (!ending) return;
    if (visibleLines >= lines.length) {
      const timer = setTimeout(() => setShowCredits(true), 3000);
      return () => clearTimeout(timer);
    }

    const line = lines[visibleLines];
    const delay = line === '' ? 600 : 800 + line.length * 30;

    const timer = setTimeout(() => {
      setVisibleLines(v => v + 1);
    }, delay);

    return () => clearTimeout(timer);
  }, [visibleLines, lines, ending]);

  // Auto-scroll to the latest line
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [visibleLines, showCredits]);

  if (!ending) return null;

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}分${s}秒`;
  };

  return (
    <div style={{
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      background: state.endingType === 'ending_c'
        ? 'linear-gradient(180deg, #0a0a1e 0%, #1a1a3e 50%, #2a2a4e 100%)'
        : 'linear-gradient(180deg, #0a0a0a 0%, #1a0a0a 100%)',
      color: '#ccc',
      fontFamily: '"Noto Serif JP", serif',
      padding: '2rem',
      paddingTop: '20vh',
      boxSizing: 'border-box',
      overflowY: 'auto',
    }}>
      {/* Title */}
      <h1 style={{
        fontSize: 'clamp(1.5rem, 6vw, 2.5rem)',
        color: state.endingType === 'ending_c' ? '#c8a87c' : '#888',
        letterSpacing: '0.3em',
        marginBottom: '0.5rem',
        textAlign: 'center',
      }}>
        {ending.title}
      </h1>
      <p style={{
        fontSize: '1.1rem',
        color: '#666',
        letterSpacing: '0.2em',
        marginBottom: '3rem',
      }}>
        {ending.subtitle}
      </p>

      {/* Story text */}
      <div style={{
        maxWidth: '600px',
        width: '100%',
        marginBottom: '2rem',
      }}>
        {lines.slice(0, visibleLines).map((line, i) => (
          <p key={i} style={{
            fontSize: 'clamp(1.05rem, 3vw, 1.25rem)',
            lineHeight: 2.2,
            margin: '0.2rem 0',
            opacity: i >= visibleLines - 3 ? 1 : 0.6,
            transition: 'opacity 1s',
            minHeight: '1.5em',
            color: i === visibleLines - 1 ? '#ddd' : '#999',
          }}>
            {line}
          </p>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Credits */}
      {showCredits && (
        <div style={{
          textAlign: 'center',
          opacity: 1,
          animation: 'fadeInSlow 3s ease-in',
          marginTop: '2rem',
          marginBottom: '4rem',
        }}>
          <div style={{
            borderTop: '1px solid #333',
            paddingTop: '2rem',
            marginBottom: '2rem',
          }}>
            <p style={{ color: '#555', fontSize: '1rem', marginBottom: '1rem' }}>
              プレイ時間: {formatTime(state.playtime)}
            </p>
            <p style={{ color: '#555', fontSize: '1rem', marginBottom: '0.3rem' }}>
              探索率: {state.explorationRate}%
            </p>
            <p style={{ color: '#555', fontSize: '1rem', marginBottom: '0.3rem' }}>
              謎解き: {state.solvedPuzzles.length}/12
            </p>
            <p style={{ color: '#555', fontSize: '0.8rem' }}>
              日記: {state.diaryFragmentsFound}/5
            </p>
          </div>

          <p style={{
            color: '#444',
            fontSize: '0.95rem',
            letterSpacing: '0.3em',
            marginBottom: '3rem',
          }}>
            残響の校舎 ― Echo of the Lost ―
          </p>

          {state.endingType !== 'ending_c' && (
            <p style={{
              color: '#666',
              fontSize: '1rem',
              fontStyle: 'italic',
              marginBottom: '2rem',
            }}>
              {state.endingType === 'ending_a'
                ? '……もっと深く探索すれば、別の結末が見えるかもしれない。'
                : '……すべての日記を集めれば、真のエンディングに辿り着けるかもしれない。'}
            </p>
          )}

          <button
            onClick={() => actions.resetGame()}
            style={{
              padding: '0.8rem 2rem',
              fontSize: '1.1rem',
              background: 'transparent',
              border: '1px solid #555',
              color: '#888',
              cursor: 'pointer',
              fontFamily: 'inherit',
              letterSpacing: '0.3em',
              borderRadius: '4px',
            }}
          >
            タイトルに戻る
          </button>
        </div>
      )}

      <style>{`
        @keyframes fadeInSlow {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
};
